'use client'

import { useState } from 'react'
import { Post } from '@/payload-types'
import { stringify } from 'qs-esm'
import { Locale } from '@/providers/Locale/types'
import { Where } from 'payload'

type UseLatestPostsOptions = {
    initialNextPage?: number
    initialStartTime?: number | null
    pageSize?: number
    locale: Locale
}

/**
 * Custom hook to load posts with pagination
 * @param initialPosts - Initial posts loaded server-side
 * @param opts - Options for the hook
 * @param opts.pageSize - Number of posts to load per page
 * @param opts.initialNextPage - Initial page number (default: 1)
 * @param opts.initialStartTime - Initial start time to query from (default: Date.now())
 * @returns object to satisfy `useInfiniteScroll` { loading, posts, hasNextPage, error, fetchMorePosts }
 */
export function useLatestPosts(initialPosts: Array<Post> = [], opts: UseLatestPostsOptions) {
    const [loading, setLoading] = useState(false)
    const [posts, setPosts] = useState<Array<Array<Post>>>([initialPosts])
    const [hasNextPage, setHasNextPage] = useState(true)
    const [error, setError] = useState(false)
    const [page, setPage] = useState(opts?.initialNextPage || 1)
    const [startTime, setStartTime] = useState<number | null>(opts?.initialStartTime || Date.now())

    const fetchMorePosts = async () => {
        if (loading || !hasNextPage) return

        setLoading(true)
        setError(false)

        try {
            const query = {
                page,
                depth: 1,
                limit: opts.pageSize || 6,
                sort: ['-updatedAt', '-publishedAt'],
                locale: opts.locale,
                'fallback-locale': 'none',
                where: {
                    and: [
                        {
                            publishedAt: {
                                less_than: startTime,
                            },
                        },
                        {
                            slug: {
                                not_equals: '',
                            },
                        },
                        {
                            slug: {
                                exists: true,
                            },
                        },
                    ],
                } satisfies Where,
            }

            const stringifiedQuery = stringify(query, {
                addQueryPrefix: true,
            })

            const res = await fetch(`/api/posts${stringifiedQuery}`)
            if (!res.ok) {
                throw new Error('Failed to fetch posts')
            }
            const data = await res.json()
            const { docs: newPosts, hasNextPage: newHasNextPage, nextStartTime } = data

            if (newPosts.length === 0) {
                setHasNextPage(false)
            } else {
                setPosts((prevPosts) => [...prevPosts.filter((posts) => posts.length > 0), newPosts])
                setHasNextPage(newHasNextPage || !!nextStartTime)
            }

            if (nextStartTime) {
                setStartTime(nextStartTime)
            } else if (newHasNextPage) {
                setPage((prevPage) => prevPage + 1)
            }
        } catch (_err) {
            setError(true)
            setHasNextPage(false)
        }

        setLoading(false)
    }

    return { loading, posts, hasNextPage, error, fetchMorePosts }
}
